



























import { Component, Vue } from 'vue-property-decorator';
import MapRoute from './MapRoute.vue';
import { bus } from '@/services/busService';
import { searchMod } from '@/store/modules/search';
import { routeMod } from '@/store/modules/route';
import { GeoCoordinate, Route, RouteStop, SearchResponse, SearchResultType } from '@/api/types';
import { MarkerType, MapViewBounds, MarkerInfoWindow } from '@/types/extendedMapOptions';
import ExtendedMapOptions from '@/types/extendedMapOptions';
import { InfoWindowReference } from '@/types/mapInfoWindow';
import { getMapElementColour } from '@/common/utils/colour';
import { isCompletedOrClosed } from '@/common/utils/routes';
import { getTableKey } from '@/common/utils/searchResult';
import { trackingMod } from '@/store/modules/tracking';
import AssetMarker from './AssetMarker.vue';
import GoogleMapInfoWindow from '@/components/GoogleMapInfoWindow.vue';
import { toLatLng } from '@/common/utils/googleMaps';

@Component({
  components: {
    MapRoute,
    AssetMarker,
    GoogleMapInfoWindow
  },
  methods: {
    getMapElementColour
  }
})
export default class GoogleMap extends Vue {
  $mapObject: google.maps.Map | null = null;
  $mapPromise: Promise<google.maps.Map> | null = null;

  private mapCenter: google.maps.LatLngLiteral = {
    lat: -36.8494511,
    lng: 174.7653064
  };

  private mapOptions: ExtendedMapOptions = {
    mapTypeControl: false,
    mapId: process.env.VUE_APP_GOOGLE_MAPS_STYLE_ID,
    useStaticMap: true,
    fullscreenControlOptions: {
      position: 9
    },
    clickableIcons: false
  };

  private markerInfoWindowData: MarkerInfoWindow = {
    data: null,
    type: null,
    deliveryRunReference: null,
    fleetNumber: null,
    routeStopIndex: null,
    position: null,
    open: false,
    options: {
      minWidth: 400,
      maxWidth: 400,
      pixelOffset: {
        width: 0,
        height: -42
      }
    }
  };

  get visibleReference(): string[] {
    return searchMod.getVisibleItems.map((item: SearchResponse) => getTableKey(item));
  }

  get getSearchResults(): SearchResponse[] {
    return searchMod.getSearchResults;
  }

  get getRoutes(): Route[] {
    return routeMod.getRoutes;
  }

  private visibility(item: SearchResponse): boolean {
    return !!item && this.visibleReference.includes(getTableKey(item));
  }

  private openMarkerInfoWindow(routeStop: RouteStop, infoWindowReference: InfoWindowReference): void {
    this.markerInfoWindowData.position = { lat: routeStop.location.latitude, lng: routeStop.location.longitude };
    this.markerInfoWindowData.open = true;
    this.markerInfoWindowData.deliveryRunReference = infoWindowReference.deliveryRunReference;
    this.markerInfoWindowData.fleetNumber = infoWindowReference.fleetNumber;
    this.markerInfoWindowData.data = routeStop;
    this.markerInfoWindowData.type = MarkerType.routeStop;
  }

  private openVehicleMarkerInfoWindow(item: SearchResponse, position: any): void {
    this.markerInfoWindowData.position = position;
    this.markerInfoWindowData.open = true;
    this.markerInfoWindowData.fleetNumber = item.fleetNumber;
    this.markerInfoWindowData.data = item;
    this.markerInfoWindowData.type = MarkerType.trackable;
  }

  private closeInfoWindow(): void {
    this.markerInfoWindowData.open = false;
  }

  private getRouteByReference(deliveryRunReference: string): Route | undefined {
    return this.getRoutes.find((route: Route) => route.deliveryRunReference === deliveryRunReference);
  }

  private routeAndVehicleColor(index: number, item: SearchResponse): string {
    const routeStatus =
      item.searchResultType === SearchResultType.route
        ? this.getRoutes.find((route) => route.deliveryRunReference === item.deliveryRunReference)?.routeStatus
        : undefined;
    return isCompletedOrClosed(routeStatus) ? '#757576' : getMapElementColour(index);
  }

  private trackableAssetLocation(trackingDeviceIdentifier: string): google.maps.LatLngLiteral | null {
    const location = trackingMod.getTrackableAssetLocation(trackingDeviceIdentifier);

    if (location) {
      return toLatLng(location);
    }

    return null;
  }

  private trackableAssetHeading(trackingDeviceIdentifier: string): number | null {
    return trackingMod.getTrackableAssetHeading(trackingDeviceIdentifier);
  }

  private isRoute(item: SearchResponse): boolean {
    return item.searchResultType === SearchResultType.route;
  }

  private isTrackable(item: SearchResponse): boolean {
    return item.searchResultType === SearchResultType.trackable;
  }

  mounted() {
    (this.$refs.mapRef as GoogleMap).$mapPromise?.then((map: google.maps.Map) => {
      this.$mapObject = map;
    });

    bus.$on('panTo', (location: GeoCoordinate) => {
      this.$mapObject?.panTo(toLatLng(location));
    });

    bus.$on('fitBounds', (bounds: MapViewBounds) => {
      this.$mapObject?.fitBounds(bounds);
    });

    bus.$on('openStopMarkerInfo', (stop: RouteStop, infoWindowReference: InfoWindowReference) => {
      this.openMarkerInfoWindow(stop, infoWindowReference);
    });

    bus.$on('openVehicleMarkerInfo', (item: SearchResponse, position: google.maps.LatLngLiteral) => {
      this.openVehicleMarkerInfoWindow(item, position);
    });

    bus.$on('closeInfoWindow', (infoWindowReference: InfoWindowReference | null) => {
      if (
        infoWindowReference === null ||
        infoWindowReference?.fleetNumber === this.markerInfoWindowData.fleetNumber ||
        infoWindowReference?.deliveryRunReference === this.markerInfoWindowData.fleetNumber
      ) {
        this.closeInfoWindow();
      }
    });
  }
}
